import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatoGuardiaBadge, ScadenzaBadge } from "@/components/StatoBadge";
import { GuardiaFormDialog } from "@/components/GuardiaFormDialog";
import { BulkEditDialog } from "@/components/BulkEditDialog";
import { Search, ChevronRight, Plus, Layers, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const GRADI = [
  "Dirigente Superiore", "Dirigente", "Com. Territoriale", "Com. Distaccamento",
  "Sottufficiale", "Agente", "Agente scelto", "Operatore",
];

export default function Anagrafica() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["admin", "coordinatore"].includes(user?.role);
  const [search, setSearch] = useState("");
  const [distaccamento, setDistaccamento] = useState("tutti");
  const [searchParams] = useSearchParams();
  const [stato, setStato] = useState(() => (["attiva", "disattivata"].includes(searchParams.get("stato")) ? searchParams.get("stato") : "tutti"));
  const [grado, setGrado] = useState("tutti");
  const [quota, setQuota] = useState("tutti");
  const [selected, setSelected] = useState([]);

  const { data: distaccamenti = [] } = useQuery({
    queryKey: ["distaccamenti"],
    queryFn: async () => (await api.get("/guardie/distaccamenti")).data,
  });

  const { data: conteggioGradi = {} } = useQuery({
    queryKey: ["conteggio-gradi", stato, distaccamento],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (stato !== "tutti") params.set("stato", stato);
      if (distaccamento !== "tutti") params.set("distaccamento", distaccamento);
      return (await api.get(`/guardie/conteggio-gradi?${params.toString()}`)).data;
    },
  });

  const { data: conteggioDist = {} } = useQuery({
    queryKey: ["conteggio-distaccamenti", stato, grado],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (stato !== "tutti") params.set("stato", stato);
      if (grado !== "tutti") params.set("grado", grado);
      return (await api.get(`/guardie/conteggio-distaccamenti?${params.toString()}`)).data;
    },
  });

  const { data: guardie = [], isLoading } = useQuery({
    queryKey: ["guardie", search, distaccamento, stato, grado, quota],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (distaccamento !== "tutti") params.set("distaccamento", distaccamento);
      if (stato !== "tutti") params.set("stato", stato);
      if (grado !== "tutti") params.set("grado", grado);
      if (quota !== "tutti") params.set("quota", quota);
      return (await api.get(`/guardie?${params.toString()}`)).data;
    },
  });

  const filteredIds = useMemo(() => guardie.map((g) => g.id), [guardie]);
  const selectedVisible = selected.filter((id) => filteredIds.includes(id));
  const allSelected = filteredIds.length > 0 && selectedVisible.length === filteredIds.length;

  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(allSelected ? [] : filteredIds);
  const clearSelection = () => setSelected([]);

  const [sendingReminder, setSendingReminder] = useState(false);
  const sendQuotaReminder = async () => {
    if (!window.confirm("Inviare un promemoria di pagamento a tutte le guardie attive con quota non pagata?")) return;
    setSendingReminder(true);
    try {
      const { data } = await api.post("/payments/quota-reminder");
      if (!data.email_configurata) {
        toast.error(`Email non configurata: 0 promemoria inviati. Guardie non in regola: ${data.non_pagate}.`);
      } else if (data.inviati === 0) {
        toast.warning(`Nessun promemoria inviato (invio email non riuscito). Guardie non in regola: ${data.non_pagate}.`);
      } else {
        toast.success(`Promemoria inviati: ${data.inviati}/${data.destinatari}. Non in regola: ${data.non_pagate}.`);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSendingReminder(false);
    }
  };

  return (
    <div className="gz-fade-up">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Anagrafica guardie</h1>
          <p className="mt-1 text-sm text-slate-500">
            {isManager ? "Sincronizzata da Excel (chiave: Codice Fiscale). Puoi aggiungere o correggere i dati manualmente." : "Elenco guardie del corpo."}
          </p>
        </div>
        {isManager && (
          <GuardiaFormDialog
            trigger={
              <Button className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="new-guardia-button">
                <Plus className="mr-2 h-4 w-4" /> Nuova guardia
              </Button>
            }
          />
        )}
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="guard-search-input"
            placeholder="Cerca nome, CF, matricola..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={distaccamento} onValueChange={setDistaccamento}>
          <SelectTrigger data-testid="filter-distaccamento"><SelectValue placeholder="Distaccamento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i distaccamenti ({conteggioDist.__total__ ?? 0})</SelectItem>
            {distaccamenti.map((d) => <SelectItem key={d} value={d}>{d} ({conteggioDist[d] ?? 0})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={grado} onValueChange={setGrado}>
          <SelectTrigger data-testid="filter-grado"><SelectValue placeholder="Ruolo / Grado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i gradi ({conteggioGradi.__total__ ?? 0})</SelectItem>
            {GRADI.map((gr) => <SelectItem key={gr} value={gr}>{gr} ({conteggioGradi[gr] ?? 0})</SelectItem>)}
            <SelectItem value="__nessuno__">Senza grado ({conteggioGradi[""] ?? 0})</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stato} onValueChange={setStato}>
          <SelectTrigger data-testid="filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli stati</SelectItem>
            <SelectItem value="attiva">Attive</SelectItem>
            <SelectItem value="disattivata">Non attive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={quota} onValueChange={setQuota}>
          <SelectTrigger data-testid="filter-quota"><SelectValue placeholder="Quota" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Quota: tutte</SelectItem>
            <SelectItem value="pagata">Quota pagata</SelectItem>
            <SelectItem value="non_pagata">Quota non pagata</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Conteggio schede per grado */}
      <div className="mb-5 flex flex-wrap gap-2" data-testid="conteggio-gradi">
        <button
          type="button"
          onClick={() => setGrado("tutti")}
          data-testid="conteggio-grado-tutti"
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${grado === "tutti" ? "border-[#14382C] bg-[#14382C] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#14382C]"}`}
        >
          Totale <span className="font-bold">{conteggioGradi.__total__ ?? 0}</span>
        </button>
        {GRADI.map((gr) => (
          <button
            key={gr}
            type="button"
            onClick={() => setGrado(gr)}
            data-testid={`conteggio-grado-${gr}`}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${grado === gr ? "border-[#14382C] bg-[#14382C] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#14382C]"}`}
          >
            {gr} <span className="font-bold">{conteggioGradi[gr] ?? 0}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setGrado("__nessuno__")}
          data-testid="conteggio-grado-nessuno"
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${grado === "__nessuno__" ? "border-[#14382C] bg-[#14382C] text-white" : "border-dashed border-slate-300 bg-white text-slate-500 hover:border-[#14382C]"}`}
        >
          Senza grado <span className="font-bold">{conteggioGradi[""] ?? 0}</span>
        </button>
      </div>

      {isManager && guardie.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="bulk-toolbar">
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <span className="font-medium">
              {selectedVisible.length > 0 ? `${selectedVisible.length} selezionate` : `${filteredIds.length} guardie filtrate`}
            </span>
            {selectedVisible.length > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-slate-500" onClick={clearSelection} data-testid="clear-selection-button">
                <X className="mr-1 h-3.5 w-3.5" /> Deseleziona
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="border-[#D97706] text-[#D97706] hover:bg-amber-50 hover:text-[#D97706]"
              disabled={sendingReminder}
              data-testid="send-quota-reminder-button"
              onClick={sendQuotaReminder}
            >
              {sendingReminder ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Promemoria quota
            </Button>
            <BulkEditDialog
              selectedIds={selectedVisible}
              filteredIds={filteredIds}
              onDone={clearSelection}
              trigger={
                <Button variant="outline" className="border-[#14382C] text-[#14382C] hover:bg-emerald-50" data-testid="bulk-edit-button">
                  <Layers className="mr-2 h-4 w-4" /> Modifica massiva
                </Button>
              }
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-400">Caricamento…</p>
      ) : guardie.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-sm text-slate-400">Nessuna guardia trovata. Importa l'anagrafica dalla pagina Sincronizza.</CardContent></Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  {isManager && (
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" className="h-4 w-4 accent-[#14382C]" checked={allSelected} onChange={toggleAll} data-testid="select-all-checkbox" aria-label="Seleziona tutte" />
                    </th>
                  )}
                  <th className="px-4 py-3">Nominativo</th>
                  <th className="px-4 py-3">Codice Fiscale</th>
                  <th className="px-4 py-3">Distaccamento</th>
                  <th className="px-4 py-3">Tessera</th>
                  <th className="px-4 py-3">Scad. nomina</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {guardie.map((g) => (
                  <tr
                    key={g.id}
                    data-testid={`guard-row-${g.id}`}
                    className="cursor-pointer transition-colors hover:bg-emerald-50/50"
                    onClick={() => navigate(`/guardie/${g.id}`)}
                  >
                    {isManager && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="h-4 w-4 accent-[#14382C]" checked={selected.includes(g.id)} onChange={() => toggleOne(g.id)} data-testid={`select-guard-${g.id}`} aria-label="Seleziona guardia" />
                      </td>
                    )}
                    <td className="px-4 py-3 font-semibold text-slate-900">{g.cognome} {g.nome}<div className="text-xs font-normal text-slate-400">Matr. {g.matricola || "—"}</div></td>
                    <td className="px-4 py-3 font-mono-code text-xs text-slate-600">{g.codice_fiscale}</td>
                    <td className="px-4 py-3 text-slate-700">{g.distaccamento || "—"}{g.provincia && <span className="ml-1 text-xs font-semibold text-slate-400" data-testid={`guard-prov-${g.id}`}>({g.provincia})</span>}</td>
                    <td className="px-4 py-3 text-slate-700">{g.numero_tessera || "—"}</td>
                    <td className="px-4 py-3"><ScadenzaBadge giorni={g.giorni_nomina} /></td>
                    <td className="px-4 py-3"><StatoGuardiaBadge stato={g.stato} /></td>
                    <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {guardie.map((g) => (
              <Card key={g.id} data-testid={`guard-card-${g.id}`} className="border-slate-200 shadow-sm">
                <CardContent className="flex items-start gap-3 p-4">
                  {isManager && (
                    <input type="checkbox" className="mt-1 h-4 w-4 flex-shrink-0 accent-[#14382C]" checked={selected.includes(g.id)} onChange={() => toggleOne(g.id)} data-testid={`select-guard-mobile-${g.id}`} aria-label="Seleziona guardia" />
                  )}
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/guardie/${g.id}`)}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{g.cognome} {g.nome}</p>
                        <p className="font-mono-code text-xs text-slate-500">{g.codice_fiscale}</p>
                      </div>
                      <StatoGuardiaBadge stato={g.stato} />
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                      <span>{g.distaccamento || "—"}{g.provincia ? ` (${g.provincia})` : ""}</span>
                      <ScadenzaBadge giorni={g.giorni_nomina} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
